/**
 * FileTree — shows files modified on the current build-agent branch.
 *
 * Groups files by directory. Color-coded by status:
 *   green  = added
 *   yellow = modified
 *   red    = deleted
 *
 * Used in the BuildAgentPanel sidebar when code mode is active.
 */

import { FilePlus, FileX, FileCode, GitBranch } from "lucide-react";

export interface FileTreeEntry {
  path: string;
  status: "added" | "modified" | "deleted";
}

interface Props {
  branch: string;
  files: FileTreeEntry[];
  onFileClick?: (path: string) => void;
}

function groupByDir(files: FileTreeEntry[]): Map<string, FileTreeEntry[]> {
  const map = new Map<string, FileTreeEntry[]>();
  for (const f of files) {
    const parts = f.path.split("/");
    const dir = parts.length > 1 ? parts.slice(0, -1).join("/") : ".";
    if (!map.has(dir)) map.set(dir, []);
    map.get(dir)!.push(f);
  }
  return map;
}

function StatusIcon({ status }: { status: FileTreeEntry["status"] }) {
  if (status === "added") return <FilePlus className="h-3 w-3 text-green-500 shrink-0" />;
  if (status === "deleted") return <FileX className="h-3 w-3 text-destructive shrink-0" />;
  return <FileCode className="h-3 w-3 text-yellow-500 shrink-0" />;
}

function statusLabel(s: FileTreeEntry["status"]) {
  if (s === "added") return "text-green-600 dark:text-green-400";
  if (s === "deleted") return "text-destructive";
  return "text-yellow-600 dark:text-yellow-400";
}

export function FileTree({ branch, files, onFileClick }: Props) {
  if (!files.length) return null;

  const grouped = groupByDir(files);

  return (
    <div className="rounded-lg border border-border bg-card overflow-hidden text-xs">
      {/* Branch header */}
      <div className="flex items-center gap-1.5 px-3 py-2 border-b border-border bg-muted/30">
        <GitBranch className="h-3 w-3 text-muted-foreground" />
        <span className="font-mono text-xs text-muted-foreground truncate">{branch}</span>
        <span className="ml-auto text-muted-foreground">{files.length} file{files.length !== 1 ? "s" : ""}</span>
      </div>

      {/* File list grouped by directory */}
      <div className="py-1">
        {Array.from(grouped.entries()).map(([dir, dirFiles]) => (
          <div key={dir}>
            {dir !== "." && (
              <div className="px-3 py-0.5 text-muted-foreground/60 font-mono">{dir}/</div>
            )}
            {dirFiles.map((f) => {
              const fileName = f.path.split("/").pop() ?? f.path;
              return (
                <button
                  key={f.path}
                  className="w-full flex items-center gap-2 px-3 py-0.5 hover:bg-muted/30 transition-colors text-left"
                  onClick={() => onFileClick?.(f.path)}
                >
                  <StatusIcon status={f.status} />
                  <span className={`font-mono truncate ${statusLabel(f.status)}`}>{fileName}</span>
                </button>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );
}
